"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { ChefHat, Mail, Lock, User } from "lucide-react";
import { z } from "zod";

import StructuredData from "@/components/StructuredData";
import { generatePageTitle, generateMetaDescription, generateKeywordsString } from "@/lib/seoUtils";

const signUpSchema = z.object({
  email: z.string().email("כתובת אימייל לא תקינה"),
  password: z.string().min(6, "הסיסמה חייבת להכיל לפחות 6 תווים"),
  displayName: z.string().min(2, "השם חייב להכיל לפחות 2 תווים"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "הסיסמאות אינן תואמות",
  path: ["confirmPassword"],
});

const signInSchema = z.object({
  email: z.string().email("כתובת אימייל לא תקינה"),
  password: z.string().min(1, "יש להזין סיסמה"),
});

const Auth = () => {
  const { signUp, signIn, signInWithGoogle, resetPassword } = useAuth();
  const { toast } = useToast();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const [signUpData, setSignUpData] = useState({
    email: "",
    password: "",
    displayName: "",
    confirmPassword: "",
  });

  const [signInData, setSignInData] = useState({
    email: "",
    password: "",
  });

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validatedData = signUpSchema.parse(signUpData);
      const { error } = await signUp(validatedData.email, validatedData.password, validatedData.displayName);

      if (error) {
        toast({
          title: "שגיאה ברישום",
          description: error.message === "User already registered"
            ? "המשתמש כבר רשום במערכת"
            : error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "רישום הושלם בהצלחה!",
          description: "ברוכים הבאים למתכונים חכמים",
        });
        router.push("/");
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "שגיאה בנתונים",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validatedData = signInSchema.parse(signInData);
      const { error } = await signIn(validatedData.email, validatedData.password);

      if (error) {
        toast({
          title: "שגיאה בהתחברות",
          description: error.message === "Invalid login credentials"
            ? "פרטי התחברות שגויים"
            : error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "התחברות הושלמה בהצלחה!",
          description: "ברוכים השבים",
        });
        router.push("/");
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "שגיאה בנתונים",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!resetEmail || !resetEmail.includes("@")) {
        toast({
          title: "שגיאה",
          description: "יש להזין כתובת אימייל תקינה",
          variant: "destructive",
        });
        return;
      }

      const { error } = await resetPassword(resetEmail);

      if (error) {
        toast({
          title: "שגיאה בשחזור סיסמה",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "הבקשה נשלחה בהצלחה!",
          description: "קישור לאיפוס סיסמה נשלח לכתובת האימייל שלך",
        });
        setResetDialogOpen(false);
        setResetEmail("");
      }
    } catch (error) {
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בשליחת הבקשה. נסה שוב מאוחר יותר.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);

    try {
      const { error } = await signInWithGoogle();

      if (error) {
        toast({
          title: "שגיאה בהתחברות",
          description: "אירעה שגיאה בהתחברות עם Google. נסה שוב מאוחר יותר.",
          variant: "destructive",
        });
        setGoogleLoading(false);
      }
      // If successful, user will be redirected by OAuth flow
    } catch (error) {
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בהתחברות עם Google. נסה שוב מאוחר יותר.",
        variant: "destructive",
      });
      setGoogleLoading(false);
    }
  };

  const pageTitle = "התחברות והרשמה - Feedly";
  const metaDescription = generateMetaDescription(
    "התחברות והרשמה",
    "התחברו או הירשמו ל-Feedly כדי ליצור מתכונים מותאמים אישית מהמצרכים שלכם. הרשמה חינמית ופשוטה"
  );

  return (
    <>
      {/* Metadata moved to metadata export or layout */}

      {/* Website structured data */}
      <StructuredData type="website" />

      <div className="min-h-screen bg-gradient-card flex items-center justify-center p-4" dir="rtl">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link href="/" className="flex items-center justify-center gap-2 text-3xl font-bold text-primary mb-4">
              <ChefHat className="h-10 w-10" />
              <span>מתכונים חכמים</span>
            </Link>
            <p className="text-muted-foreground">התחברו או הירשמו כדי להתחיל</p>
          </div>

          <Card className="shadow-glow border-0">
            <CardHeader>
              <CardTitle className="text-center text-2xl">כניסה למערכת</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="signin" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="signin">התחברות</TabsTrigger>
                  <TabsTrigger value="signup">רישום</TabsTrigger>
                </TabsList>

                <TabsContent value="signin" className="space-y-4">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">אימייל</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signin-email"
                          type="email"
                          placeholder="הזינו את כתובת האימייל"
                          value={signInData.email}
                          onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signin-password">סיסמה</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signin-password"
                          type="password"
                          placeholder="הזינו את הסיסמה"
                          value={signInData.password}
                          onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={loading} variant="hero">
                      {loading ? "מתחבר..." : "התחברות"}
                    </Button>

                    <div className="text-center mt-4">
                      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="link" className="text-sm text-muted-foreground hover:text-primary">
                            שכחת סיסמה?
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md" dir="rtl">
                          <DialogHeader>
                            <DialogTitle className="text-right">שחזור סיסמה</DialogTitle>
                            <DialogDescription className="text-right">
                              הזן את כתובת האימייל שלך ונשלח לך קישור לאיפוס הסיסמה
                            </DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleResetPassword} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="reset-email">אימייל</Label>
                              <div className="relative">
                                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                  id="reset-email"
                                  type="email"
                                  placeholder="הזינו את כתובת האימייל"
                                  value={resetEmail}
                                  onChange={(e) => setResetEmail(e.target.value)}
                                  className="pl-10"
                                  required
                                />
                              </div>
                            </div>
                            <div className="flex gap-3 justify-end">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setResetDialogOpen(false)}
                                disabled={loading}
                              >
                                ביטול
                              </Button>
                              <Button type="submit" disabled={loading} variant="hero">
                                {loading ? "שולח..." : "שלח קישור"}
                              </Button>
                            </div>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="signup" className="space-y-4">
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">שם מלא</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder="הזינו את השם המלא"
                          value={signUpData.displayName}
                          onChange={(e) => setSignUpData({ ...signUpData, displayName: e.target.value })}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-email">אימייל</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="הזינו את כתובת האימייל"
                          value={signUpData.email}
                          onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-password">סיסמה</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-password"
                          type="password"
                          placeholder="בחרו סיסמה (לפחות 6 תווים)"
                          value={signUpData.password}
                          onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-confirm">אימות סיסמה</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-confirm"
                          type="password"
                          placeholder="הזינו שוב את הסיסמה"
                          value={signUpData.confirmPassword}
                          onChange={(e) => setSignUpData({ ...signUpData, confirmPassword: e.target.value })}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={loading} variant="hero">
                      {loading ? "נרשם..." : "רישום"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-muted-foreground">או</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full h-11 gap-2 border-gray-300 hover:bg-gray-50"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {googleLoading ? "מתחבר..." : "התחברות עם Google"}
              </Button>

              <div className="text-center mt-6">
                <Button variant="link" asChild>
                  <Link href="/">חזרה לעמוד הבית</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Auth;